import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Check, ChevronDown } from 'lucide-react';

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import {
  type InputWidth,
  type InteractiveSize,
  type ValidationState,
  getIconPixels,
  inputBaseClasses,
  inputWidthSizes,
  interactiveSizes,
  validationBorderClasses,
  validationRingClasses,
} from '@/theme/size-tokens';

export type { ValidationState };

export type ComboboxOption = {
  value: string;
  label: string;
  description?: string;
};

/** A labelled set of options rendered under a non-interactive group heading. */
export type ComboboxOptionGroup = {
  label: string;
  options: ComboboxOption[];
};

export type ComboboxCommitDetails = {
  source: 'option' | 'free-text';
};

/** True when the option matches the (already-lowercased) query. */
function comboboxOptionMatches(
  option: ComboboxOption,
  lowerQuery: string
): boolean {
  return (
    option.label.toLowerCase().includes(lowerQuery) ||
    option.value.toLowerCase().includes(lowerQuery) ||
    (option.description?.toLowerCase().includes(lowerQuery) ?? false)
  );
}

export type ComboboxProps = {
  /** Suggested options shown in the dropdown */
  options?: ComboboxOption[];
  /**
   * Suggestions grouped by source. When two or more groups are supplied the
   * dropdown renders a non-interactive heading before each group; with 0 or 1
   * group it falls back to the flat `options` list. Keyboard navigation and
   * value resolution always traverse the flattened, visible options.
   */
  groups?: ComboboxOptionGroup[];
  /** Called when the user commits a value (from list or free-text) */
  onCommit: (value: string, details?: ComboboxCommitDetails) => void;
  /** Controlled input value */
  value?: string;
  /** Called when the draft input value changes */
  onValueChange?: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disables all interaction */
  disabled?: boolean;
  /** Native input type */
  inputType?: React.HTMLInputTypeAttribute;
  /** Size variant */
  size?: InteractiveSize;
  /** Width */
  width?: InputWidth;
  /** Validation state */
  validationState?: ValidationState;
  /** Text shown when nothing matches the query */
  noneFoundText?: string;
  /** Whether free-text entry is allowed (default: true) */
  allowFreeText?: boolean;
  /** Sanitize function applied to free-text input before commit */
  sanitize?: (raw: string) => string;
  /** Values already selected (filtered out of the dropdown) */
  excludeValues?: string[];
  /** HTML id */
  id?: string;
  /** aria-describedby forwarded from FormField */
  'aria-describedby'?: string;
  /** Accessible label for the listbox (defaults to "Suggestions") */
  'aria-label'?: string;
  /** Additional class names */
  className?: string;
};

function Combobox({
  options = [],
  groups,
  onCommit,
  value,
  onValueChange,
  placeholder = 'Type to search…',
  disabled = false,
  inputType = 'text',
  size = 'md',
  width,
  validationState = 'neutral',
  noneFoundText = 'No matches',
  allowFreeText = true,
  sanitize,
  excludeValues = [],
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  className,
}: Readonly<ComboboxProps>) {
  const listboxId = useId();
  const sizeConfig = interactiveSizes[size];
  const widthClass = width ? inputWidthSizes[width] : 'w-full';
  const isControlled = value !== undefined;

  const [uncontrolledDraft, setUncontrolledDraft] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const draft = isControlled ? value : uncontrolledDraft;

  const setDraft = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setUncontrolledDraft(nextValue);
      }
      onValueChange?.(nextValue);
    },
    [isControlled, onValueChange]
  );

  const excludeSet = useMemo(() => new Set(excludeValues), [excludeValues]);

  // Canonical source list, whether the caller passed groups or a flat list.
  // Group only when more than one group is supplied, mirroring the select rule;
  // otherwise every source collapses into a single flat, headingless group.
  const sourceGroups = useMemo<ComboboxOptionGroup[]>(() => {
    if (groups && groups.length > 1) {
      return groups;
    }
    return [{ label: '', options }];
  }, [groups, options]);

  const isGrouped = sourceGroups.length > 1;

  const allOptions = useMemo(
    () => sourceGroups.flatMap((group) => group.options),
    [sourceGroups]
  );

  // Filter within each group and drop empty groups, so headings only appear for
  // groups that still have matches.
  const filteredGroups = useMemo<ComboboxOptionGroup[]>(() => {
    // When the draft equals an option's label/value verbatim, the user has
    // already committed (or typed it whole); show every option so they can
    // pick a different one instead of filtering down to the single match.
    const showAll =
      !draft || allOptions.some((o) => o.label === draft || o.value === draft);
    if (showAll) {
      return sourceGroups.filter((group) => group.options.length > 0);
    }
    const lower = draft.toLowerCase();
    return sourceGroups.flatMap((group) => {
      const matched = group.options.filter((o) =>
        comboboxOptionMatches(o, lower)
      );
      return matched.length > 0 ? [{ ...group, options: matched }] : [];
    });
  }, [sourceGroups, allOptions, draft]);

  // Flattened, visible options in render order — the single source of truth for
  // keyboard navigation, the active index, and value resolution.
  const filtered = useMemo(
    () => filteredGroups.flatMap((group) => group.options),
    [filteredGroups]
  );

  // Precompute the flat index of each option so headings never shift the index
  // space that keyboard navigation and `aria-activedescendant` rely on.
  const renderGroups = useMemo(() => {
    let flatIndex = 0;
    return filteredGroups.map((group) => ({
      label: group.label,
      items: group.options.map((option) => ({ option, index: flatIndex++ })),
    }));
  }, [filteredGroups]);

  const commit = useCallback(
    (raw: string, source: ComboboxCommitDetails['source']) => {
      const value = sanitize ? sanitize(raw) : raw.trim();
      if (value && !excludeSet.has(value)) {
        const matched =
          source === 'free-text'
            ? allOptions.find((o) => o.label === value || o.value === value)
            : undefined;
        if (matched) {
          onCommit(matched.value, { source: 'option' });
        } else {
          onCommit(value, { source });
        }
      }
      if (!isControlled) {
        setUncontrolledDraft('');
        onValueChange?.('');
      }
      setIsOpen(false);
      setActiveIndex(-1);
    },
    [allOptions, excludeSet, isControlled, onCommit, onValueChange, sanitize]
  );

  const tryCommitActiveOption = useCallback((): boolean => {
    if (activeIndex < 0 || activeIndex >= filtered.length) return false;
    const opt = filtered[activeIndex];
    if (excludeSet.has(opt.value)) return false;
    commit(opt.value, 'option');
    return true;
  }, [activeIndex, commit, excludeSet, filtered]);

  // Dismissal (outside pointer-down / focus leaving the layer) is owned by the
  // popover's DismissableLayer. Mirror the previous outside-click behavior:
  // commit the active option, else the free-text draft, before it closes.
  // Pointer-downs inside the input keep the popup open.
  const handleInteractOutside = useCallback(
    (event: { target: EventTarget | null; preventDefault: () => void }) => {
      if (containerRef.current?.contains(event.target as Node)) {
        event.preventDefault();
        return;
      }
      if (tryCommitActiveOption()) return;
      if (draft && allowFreeText) commit(draft, 'free-text');
    },
    [allowFreeText, commit, draft, tryCommitActiveOption]
  );

  // Scroll active option into view
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const el = document.getElementById(`${listboxId}-option-${activeIndex}`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen, listboxId]);

  const shouldShowDropdown =
    isOpen &&
    allOptions.length > 0 &&
    (filtered.length > 0 || draft.length > 0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw.endsWith(',') && allowFreeText) {
      commit(raw.slice(0, -1), 'free-text');
      return;
    }
    const nextDraft = sanitize ? sanitize(raw) : raw;

    setDraft(nextDraft);
    setActiveIndex(-1);
    // A non-empty draft always shows the dropdown (to surface "no matches"),
    // otherwise open only when there are options to suggest.
    if (nextDraft.length > 0 || allOptions.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (tryCommitActiveOption()) return;
      if (allowFreeText && draft) {
        commit(draft, 'free-text');
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      // APG combobox pattern: first Escape closes the popup;
      // a second Escape (popup already closed) clears the draft.
      if (isOpen) {
        setIsOpen(false);
        setActiveIndex(-1);
      } else {
        setDraft('');
      }
      return;
    }

    if (e.key === 'ArrowDown' && shouldShowDropdown) {
      e.preventDefault();
      setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
      return;
    }

    if (e.key === 'ArrowUp' && shouldShowDropdown) {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
      return;
    }

    if (e.key === 'Home' && shouldShowDropdown && filtered.length > 0) {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (e.key === 'End' && shouldShowDropdown && filtered.length > 0) {
      e.preventDefault();
      setActiveIndex(filtered.length - 1);
      return;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) return;
    if (tryCommitActiveOption()) return;
    if (draft && allowFreeText) commit(draft, 'free-text');
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const renderOption = (option: ComboboxOption, idx: number) => {
    const isActive = idx === activeIndex;
    const isExcluded = excludeSet.has(option.value);
    return (
      <div
        key={option.value}
        id={`${listboxId}-option-${idx}`}
        className={`flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left ${sizeConfig.text} transition-colors ${
          isActive
            ? 'bg-accent text-accent-foreground'
            : 'hover:bg-accent hover:text-accent-foreground'
        }`}
        onMouseDown={(e) => {
          e.preventDefault();
          if (isExcluded) return;
          commit(option.value, 'option');
        }}
        onMouseEnter={() => setActiveIndex(idx)}
        role="option"
        aria-selected={isActive}
        aria-disabled={isExcluded || undefined}
        tabIndex={-1}
      >
        <div className="min-w-0 flex-1">
          <div className="truncate">{option.label}</div>
          {option.description && (
            <div className="text-muted-foreground truncate text-xs">
              {option.description}
            </div>
          )}
        </div>
        {isExcluded && (
          <Check
            size={getIconPixels(size, 'desktop')}
            className="shrink-0"
            aria-label="Already selected"
          />
        )}
      </div>
    );
  };

  const inputClasses = [
    inputBaseClasses,
    `${sizeConfig.height} px-4 pr-9 sm:px-3 sm:pr-9 ${sizeConfig.text}`,
    validationBorderClasses[validationState],
    validationRingClasses[validationState],
    widthClass,
  ].join(' ');

  return (
    <Popover
      open={shouldShowDropdown}
      onOpenChange={(open) => {
        // The anchor never toggles the popover open (opening is driven by
        // focus/typing); only honor requests to close (Escape, dismiss).
        if (!open) {
          setIsOpen(false);
          setActiveIndex(-1);
        }
      }}
    >
      <PopoverAnchor asChild>
        <div
          ref={containerRef}
          className={`relative ${widthClass}${className ? ` ${className}` : ''}`}
        >
          <input
            ref={inputRef}
            id={id}
            type={inputType}
            value={draft}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={() => {
              if (filtered.length > 0 || draft.length > 0) {
                setIsOpen(true);
              }
            }}
            onClick={() => {
              if (!isOpen && (filtered.length > 0 || draft.length > 0)) {
                setIsOpen(true);
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            className={inputClasses}
            role="combobox"
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-expanded={shouldShowDropdown}
            aria-controls={shouldShowDropdown ? listboxId : undefined}
            aria-activedescendant={
              shouldShowDropdown && activeIndex >= 0
                ? `${listboxId}-option-${activeIndex}`
                : undefined
            }
            aria-describedby={ariaDescribedBy}
            aria-invalid={validationState === 'error' || undefined}
          />
          {allOptions.length > 0 && (
            <ChevronDown
              className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 opacity-50"
              aria-hidden="true"
            />
          )}
        </div>
      </PopoverAnchor>

      {shouldShowDropdown && (
        <PopoverContent
          ref={dropdownRef}
          // Keep the content inside the anchor's DOM subtree (not a body
          // portal) so it stays within any enclosing Dialog's scroll-lock
          // allowlist and focus scope. The popper wrapper is `position: fixed`,
          // so it still escapes every ancestor `overflow` clip (e.g. a Dialog
          // body's `overflow-y-auto`) that previously cut the last option.
          portalled={false}
          align="start"
          sideOffset={4}
          collisionPadding={8}
          // Focus must remain in the input while suggestions are open.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={handleInteractOutside}
          className="bg-card border-border w-(--radix-popover-trigger-width) overflow-hidden rounded-md border p-0 shadow-lg"
          onMouseDown={(e) => {
            // Prevent focus from leaving the input when clicking
            // anywhere inside the dropdown (options, scrollbar, etc.).
            e.preventDefault();
          }}
        >
          <div
            id={listboxId}
            className="scroll-py-1 overflow-y-auto overscroll-contain py-1"
            // Cap against the collision-aware space Radix exposes for the
            // chosen side, minus the 1px top/bottom borders, so the last row
            // keeps clearance and the list scrolls when it exceeds that space.
            style={{
              maxHeight:
                'min(240px, calc(var(--radix-popover-content-available-height, 240px) - 2px))',
            }}
            role="listbox"
            aria-label={ariaLabel ?? 'Suggestions'}
            tabIndex={-1}
          >
            {filtered.length === 0 ? (
              <div
                className="text-muted-foreground px-3 py-6 text-center text-sm"
                role="status"
                aria-live="polite"
              >
                {noneFoundText}
              </div>
            ) : (
              renderGroups.map((group, groupIndex) => {
                const items = group.items.map(({ option, index }) =>
                  renderOption(option, index)
                );
                if (!isGrouped) return items;
                const labelId = `${listboxId}-group-${groupIndex}`;
                return (
                  <div key={labelId} role="group" aria-labelledby={labelId}>
                    <div
                      id={labelId}
                      data-slot="combobox-group-label"
                      role="presentation"
                      className="text-muted-foreground px-3 py-1.5 text-xs font-semibold"
                    >
                      {group.label}
                    </div>
                    {items}
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}

Combobox.displayName = 'Combobox';

export default Combobox;
